import type { IngestionState } from "./ingestion.types";
import {
  INGESTION_ACTIVE_STATES,
  INGESTION_ATTENTION_STATES,
  INGESTION_TERMINAL_STATES,
} from "./ingestion.types";

/**
 * The only legal lifecycle edges. A failed upload can retry from a known
 * checkpoint, while a completed outcome cannot silently become active again.
 */
export const INGESTION_TRANSITIONS: Readonly<
  Record<IngestionState, readonly IngestionState[]>
> = {
  idle: ["selecting"],
  selecting: ["validating", "idle", "canceled"],
  validating: [
    "queued",
    "rejected",
    "unsupported",
    "failed_retryable",
    "canceled",
  ],
  queued: ["uploading", "failed_retryable", "canceled"],
  uploading: ["uploaded", "failed_retryable", "canceled"],
  uploaded: ["processing", "source_only", "failed_retryable", "canceled"],
  processing: [
    "background_processing",
    "review_ready",
    "source_only",
    "partial_success",
    "failed_retryable",
    "canceled",
  ],
  background_processing: [
    "review_ready",
    "source_only",
    "partial_success",
    "failed_retryable",
    "canceled",
  ],
  review_ready: ["needs_review", "saving", "source_only", "partial_success"],
  needs_review: [
    "saving",
    "source_only",
    "partial_success",
    "failed_retryable",
    "canceled",
  ],
  saving: ["complete", "source_only", "partial_success", "failed_retryable"],
  complete: [],
  source_only: [],
  partial_success: [],
  failed_retryable: ["queued", "uploading", "processing", "saving", "canceled"],
  unsupported: [],
  rejected: [],
  canceled: [],
};

export function canTransitionIngestionState(
  from: IngestionState,
  to: IngestionState,
): boolean {
  // Replaying an idempotent event should not make a job invalid.
  return from === to || INGESTION_TRANSITIONS[from].includes(to);
}

export function transitionIngestionState(
  from: IngestionState,
  to: IngestionState,
): IngestionState {
  if (!canTransitionIngestionState(from, to)) {
    throw new Error(`Invalid ingestion transition: ${from} -> ${to}`);
  }

  return to;
}

export function isIngestionTerminalState(state: IngestionState): boolean {
  return INGESTION_TERMINAL_STATES.includes(state);
}

export function isIngestionActiveState(state: IngestionState): boolean {
  return INGESTION_ACTIVE_STATES.includes(state);
}

export function requiresIngestionAttention(state: IngestionState): boolean {
  return INGESTION_ATTENTION_STATES.includes(state);
}
